import React, { useCallback, useMemo, useState } from "react";
import { AutocompleteInput } from "../../components/AutocompleteInput";
import { useAuth } from "../../auth/AuthContext";
import { formatDateBR, formatMoneyBR } from "../../utils/format";

type Option = { id: number; label: string };

type PreviewItem = {
  date: string;
  idExterno: string;
  amount: number;
  currency: string;
  status: "NEW" | "DUPLICATE";
  month: number;
  year: number;
  description: string;
  rawText?: string | null;
  accountId?: number | null;
  accountLabel?: string;
  costCenterId?: number | null;
  costCenterLabel?: string;
};

type PreviewResponse = {
  card: { id: number; name: string; brand?: string | null; last4?: string | null };
  last4: string;
  total: number;
  count: number;
  period: { start: string; end: string };
  items: PreviewItem[];
};

type CommitSummary = {
  inserted: number;
  duplicated: number;
  invoices: Array<number | null>;
  total: number;
};

const API_URL = import.meta.env.VITE_API_URL as string;
const ACCESS_TOKEN_KEY = "mpf_admin_access_token";

export const MetaAdsImportPage: React.FC = () => {
  const { client } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [items, setItems] = useState<PreviewItem[]>([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingCommit, setLoadingCommit] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<CommitSummary | null>(null);
  const [defaultAccount, setDefaultAccount] = useState<Option | null>(null);
  const [defaultCostCenter, setDefaultCostCenter] = useState<Option | null>(null);

  const totals = useMemo(() => {
    const totalNew = items.filter((item) => item.status === "NEW").length;
    const totalDup = items.filter((item) => item.status === "DUPLICATE").length;
    return { totalNew, totalDup };
  }, [items]);

  const fetchAccounts = useCallback(
    async (query: string) => {
      const response = await client.get(
        `/modules/accounts?q=${encodeURIComponent(query)}&take=20&skip=0`
      );
      if (!response.ok) {
        return [];
      }
      const payload = (await response.json()) as {
        data: Array<{ id: number; code: string; name: string }>;
      };
      return payload.data.map((item) => ({
        id: item.id,
        label: `${item.code} - ${item.name}`,
      }));
    },
    [client]
  );

  const fetchCostCenters = useCallback(
    async (query: string) => {
      const response = await client.get(
        `/modules/cost-centers?q=${encodeURIComponent(query)}&take=20&skip=0`
      );
      if (!response.ok) {
        return [];
      }
      const payload = (await response.json()) as {
        data: Array<{ id: number; code?: string | null; name: string }>;
      };
      return payload.data.map((item) => ({
        id: item.id,
        label: `${item.code ?? ""} ${item.name}`.trim(),
      }));
    },
    [client]
  );

  const applyDefaultAccount = () => {
    if (!defaultAccount) {
      return;
    }
    setItems((prev) =>
      prev.map((item) => ({
        ...item,
        accountId: defaultAccount.id,
        accountLabel: defaultAccount.label,
      }))
    );
  };

  const applyDefaultCostCenter = () => {
    if (!defaultCostCenter) {
      return;
    }
    setItems((prev) =>
      prev.map((item) => ({
        ...item,
        costCenterId: defaultCostCenter.id,
        costCenterLabel: defaultCostCenter.label,
      }))
    );
  };

  const updateItem = (index: number, next: Partial<PreviewItem>) => {
    setItems((prev) =>
      prev.map((item, idx) => (idx === index ? { ...item, ...next } : item))
    );
  };

  const handlePreview = async () => {
    if (!file) {
      setError("Selecione um arquivo CSV.");
      return;
    }
    if (!file.name.toLowerCase().endsWith(".csv")) {
      setError("Arquivo invalido, use CSV");
      return;
    }
    setError(null);
    setSummary(null);
    setLoadingPreview(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const token = localStorage.getItem(ACCESS_TOKEN_KEY);
      const response = await fetch(`${API_URL}/integrations/meta-ads/preview`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: formData,
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || "Falha ao gerar preview.");
      }
      const payload = (await response.json()) as PreviewResponse;
      setPreview(payload);
      setItems(payload.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao gerar preview.");
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleCommit = async () => {
    if (!preview) {
      return;
    }
    const newItems = items.filter((item) => item.status === "NEW");
    if (newItems.length === 0) {
      setError("Nenhum lancamento novo para importar.");
      return;
    }
    setLoadingCommit(true);
    setError(null);
    try {
      const response = await client.post("/integrations/meta-ads/commit", {
        cardId: preview.card.id,
        items: newItems.map((item) => ({
          date: item.date,
          amount: item.amount,
          idExterno: item.idExterno,
          month: item.month,
          year: item.year,
          description: item.description,
          accountId: item.accountId ?? null,
          costCenterId: item.costCenterId ?? null,
          rawText: item.rawText ?? undefined,
          currency: item.currency,
        })),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || "Falha ao importar.");
      }
      const payload = (await response.json()) as CommitSummary;
      setSummary(payload);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao importar.");
    } finally {
      setLoadingCommit(false);
    }
  };

  return (
    <div className="container py-4">
      <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
        <div>
          <h1 className="mb-1">Importar Meta Ads</h1>
          <div className="text-muted small">
            Importe transacoes do CSV e vincule a fatura do cartao.
          </div>
        </div>
      </div>

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-6">
              <label className="form-label">Arquivo CSV</label>
              <input
                type="file"
                className="form-control"
                accept=".csv,text/csv"
                onChange={(event) => {
                  const next = event.target.files?.[0] ?? null;
                  if (next && !next.name.toLowerCase().endsWith(".csv")) {
                    setError("Arquivo invalido, use CSV");
                    event.currentTarget.value = "";
                    setFile(null);
                    return;
                  }
                  setError(null);
                  setFile(next);
                }}
                disabled={loadingPreview}
              />
            </div>
            <div className="col-md-3">
              <button
                type="button"
                className="btn btn-primary w-100"
                onClick={handlePreview}
                disabled={loadingPreview}
              >
                {loadingPreview ? "Processando..." : "Gerar preview"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {preview && (
        <>
          <div className="card shadow-sm mb-3">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-3">
                  <div className="text-muted small">Cartao</div>
                  <div className="fw-semibold">
                    {preview.card.name} {preview.card.last4 ? `(${preview.card.last4})` : ""}
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-muted small">Periodo</div>
                  <div className="fw-semibold">
                    {formatDateBR(preview.period.start)} - {formatDateBR(preview.period.end)}
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-muted small">Quantidade</div>
                  <div className="fw-semibold">{preview.count}</div>
                </div>
                <div className="col-md-3">
                  <div className="text-muted small">Total</div>
                  <div className="fw-semibold">{formatMoneyBR(preview.total)}</div>
                </div>
              </div>
              <div className="mt-3 row g-3">
                <div className="col-md-6">
                  <AutocompleteInput
                    label="Conta contÃ¡bil padrÃ£o"
                    placeholder="Buscar conta contÃ¡bil"
                    value={defaultAccount?.id ?? null}
                    selectedLabel={defaultAccount?.label ?? ""}
                    fetchOptions={fetchAccounts}
                    onSelect={(option) => setDefaultAccount(option)}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm mt-2"
                    onClick={applyDefaultAccount}
                    disabled={!defaultAccount}
                  >
                    Aplicar conta para todos
                  </button>
                </div>
                <div className="col-md-6">
                  <AutocompleteInput
                    label="Centro de custo padrÃ£o"
                    placeholder="Buscar centro de custo"
                    value={defaultCostCenter?.id ?? null}
                    selectedLabel={defaultCostCenter?.label ?? ""}
                    fetchOptions={fetchCostCenters}
                    onSelect={(option) => setDefaultCostCenter(option)}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm mt-2"
                    onClick={applyDefaultCostCenter}
                    disabled={!defaultCostCenter}
                  >
                    Aplicar centro de custo para todos
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="card shadow-sm">
            <div className="card-body">
              <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center mb-3">
                <div className="text-muted small">
                  Novos: {totals.totalNew} | Duplicados: {totals.totalDup}
                </div>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleCommit}
                  disabled={loadingCommit || totals.totalNew === 0}
                >
                  {loadingCommit ? "Importando..." : "Confirmar ImportaÃ§Ã£o"}
                </button>
              </div>
              <div className="table-responsive">
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>ID</th>
                      <th>Valor</th>
                      <th>Status</th>
                      <th>Conta contÃ¡bil</th>
                      <th>Centro de custo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={`${item.idExterno}-${index}`}>
                        <td>{formatDateBR(item.date)}</td>
                        <td className="text-muted">{item.idExterno}</td>
                        <td>{formatMoneyBR(item.amount)}</td>
                        <td>
                          <span
                            className={`badge ${
                              item.status === "DUPLICATE" ? "text-bg-secondary" : "text-bg-success"
                            }`}
                          >
                            {item.status === "DUPLICATE" ? "Duplicado" : "Novo"}
                          </span>
                        </td>
                        <td style={{ minWidth: 220 }}>
                          <AutocompleteInput
                            label=""
                            placeholder="Selecionar conta"
                            value={item.accountId ?? null}
                            selectedLabel={item.accountLabel ?? ""}
                            fetchOptions={fetchAccounts}
                            onSelect={(option) =>
                              updateItem(index, {
                                accountId: option?.id ?? null,
                                accountLabel: option?.label ?? "",
                              })
                            }
                          />
                        </td>
                        <td style={{ minWidth: 220 }}>
                          <AutocompleteInput
                            label=""
                            placeholder="Selecionar centro"
                            value={item.costCenterId ?? null}
                            selectedLabel={item.costCenterLabel ?? ""}
                            fetchOptions={fetchCostCenters}
                            onSelect={(option) =>
                              updateItem(index, {
                                costCenterId: option?.id ?? null,
                                costCenterLabel: option?.label ?? "",
                              })
                            }
                          />
                        </td>
                      </tr>
                    ))}
                    {items.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center text-muted">
                          Nenhuma transacao encontrada.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      {summary && (
        <div className="alert alert-success mt-3">
          ImportaÃ§Ã£o concluÃ­da. Inseridos: {summary.inserted}. Duplicados: {summary.duplicated}.{" "}
          Faturas afetadas: {summary.invoices.length}. Total inserido: {formatMoneyBR(summary.total)}.
        </div>
      )}
    </div>
  );
};
